/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponentLegalLink.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SlidingComponentLinkTemplate from "./SlidingComponentLinkTemplate";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import { HandleApiActions, labCar } from "../../../redux/actions";
import { componentStyle } from "../../../config/appTheme";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomAutoCompleteWithAPIV2 from "../../../components/customComponentsV2/common/searchBar/CustomAutoCompleteWithAPIV2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";

function VehicleComponentLegalLink({
    triggerOnSelectVehicleSystem,
    selectedVehicleSystem,
    setSelectedVehicleSystem,
    setTotalVehicleComponentCount,
}) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const zoneFromurl = queryParams.get("zone");
    const vsFromUrl = queryParams.get("vs_id");
    const vcFromUrl = queryParams.get("vc_id");

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const selectedProjectDetails = useSelector(
        (state) => state?.projectConfigReducer?.selectedProjectDetails,
    );
    const selectedSingleVariantDetails = useSelector(
        (state) => state.projectConfigReducer.selectedVariantDetails,
    );

    const [vcFromUrlState, setVcFromUrlState] = useState(null);
    const [vcMainList, setVcMainList] = useState([]);
    const [vcCount, setVcCount] = useState(0);
    const [projectID, setProjectID] = useState(null);
    const [variantID, setVariantID] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(25);
    const [cardOpen, setCardOpen] = useState(true);

    const [linkedLegalRequirement, setLinkedLegalRequirement] = useState([]);
    const [selectedVSComponentMain, setSelectedVSComponentMain] = useState({});
    const [zoneMainOptionsList, setZoneMainOptionsList] = useState([]);
    const [selectedZoneMain, setSelectedZoneMain] = useState(null);

    const [selectedVSID, setSelectedVSID] = useState(null);
    const [selectedVCModule, setSelectedVCModule] = useState({});
    const [modifiedVCList, setModifiedVCList] = useState([]);
    const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
    const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
    const [vcGetTrigger, setVcGetTrigger] = useState(false);
    const isMountedRef = useRef(false);
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
    const [pendingVCActionDialogOpen, setPendingVCActionDialogOpen] =
        useState(false);
    const [selectedPendingVCAction, setSelectedPendingVCAction] = useState(null);

    useEffect(() => {
        setVcFromUrlState(vcFromUrl);
    }, [vcFromUrl]);

    useEffect(() => {
        setSelectedVSComponentMain(null);
        setSelectedVSID(null);
        setLinkedLegalRequirement([]);
        if (selectedProjectDetails?.project_id) {
            setProjectID(selectedProjectDetails?.project_id);
        } else {
            setProjectID(null);
        }
        if (selectedSingleVariantDetails?.variant_id) {
            setVariantID(selectedSingleVariantDetails?.variant_id);
        } else {
            setVariantID(null);
        }
    }, [selectedProjectDetails, selectedSingleVariantDetails]);

    useEffect(() => {
        setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
        setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
    }, [selectedProjectDetails]);

    useEffect(() => {
        if (selectedProjectIsZonal) {
            const filteredZoneMainOptionsList = selectedProjectZonesList
                ?.map((el) => ({ value: el?.position, label: el?.zone_name }))
                ?.sort((a, b) => a.label.localeCompare(b.label));
            setZoneMainOptionsList(filteredZoneMainOptionsList);

            const zoneFromUrlValue = filteredZoneMainOptionsList?.find(
                (zone) => zone.value === zoneFromurl,
            );

            if (zoneFromUrlValue) {
                setSelectedZoneMain(zoneFromUrlValue?.value);
            } else if (
                selectedProjectIsZonal &&
                filteredZoneMainOptionsList?.[0]?.value &&
                !selectedZoneMain
            ) {
                setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
            } else if (selectedProjectIsZonal) {
                setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
            } else {
                setSelectedZoneMain(null);
            }
        }
    }, [selectedProjectZonesList, selectedProjectIsZonal, selectedZoneMain, zoneFromurl]);

    const getLegalRequirement = useCallback(async (vc_id) => {
        if (!vc_id) return;

        const resp = await Promise.resolve(dispatch(
            HandleApiActions({
                ...softwareIntersectionAPI.get_vc_legal_requirements,
                params: {
                    vc_id,
                    project_id: selectedProjectDetails?.project_id,
                    variant_id: selectedSingleVariantDetails?.variant_id,
                },
            }),
        ));

        const linkedRequirements = resp?.data?.[0]?.linked_lr_details;
        if (resp?.error || !linkedRequirements?.[0]?.lr_id) {
            setLinkedLegalRequirement([]);
            return;
        }

        setLinkedLegalRequirement(
            linkedRequirements.map(({ ls_details, ...requirement }) => ({
                ...requirement,
                ...ls_details,
            })),
        );
    }, [dispatch, selectedProjectDetails?.project_id, selectedSingleVariantDetails?.variant_id]);

    const handleSelectedComponent = useCallback((selectedObj) => {
        setSelectedVCModule(selectedObj);
        getLegalRequirement(Number(selectedObj?.id));
    }, [getLegalRequirement]);

    useEffect(() => {
        const modifiedList = vcMainList.map((item) => ({
            data: item,
            id: item.id,                          // ✅ API returns "id"
            cardDataArray: [
                {
                    key: "Component Number",
                    value: item.component_number ?? "", // ✅ real field
                    keyStyle: { fontWeight: 600 },
                    valueStyle: { fontWeight: 600 },
                },
                {
                    key: "Name",
                    value: item.name ?? "",            // ✅ real field
                },
                {
                    key: "Description",
                    value: item.description ?? "",     // ✅ fallback prevents crash
                },
            ],
        }));
        setModifiedVCList(modifiedList);
        if (modifiedList.length) {
            handleSelectedComponent(modifiedList[0]);
        } else {
            handleSelectedComponent({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vcMainList]);

    const unlinkLegalRequirement = async ({ lr_id, reason, callback }) => {
        console.log("unlinkLegalRequirement placeholder", lr_id, reason);
    };

    const handleActionDialogOpen = () => setActionDialogOpen(true);
    const handleActionDialogClose = () => setActionDialogOpen(false);
    const handleActionType1DialogOpen = () => setActionType1DialogOpen(true);
    const handleActionType1DialogClose = () => setActionType1DialogOpen(false);
    const handlePendingVCActionDialogClose = () => {
        setPendingVCActionDialogOpen(false);
        setSelectedPendingVCAction(null);
    };

    const getVCActionTypeTooltip = (actionType) => {
        switch (Number(actionType)) {
            case 1:
                return "Action Pending in Vehicle Component to Vehicle Component linking";
            case 2:
                return "Action Pending in Vehicle Component to Legal linking";
            case 4:
                return "Action required in Vehicle Component Attribute";
            case 5:
                return "Action pending in Vehicle Component Version";
            default:
                return "";
        }
    };

    const handleVCActionTypeClick = (event, item) => {
        event.stopPropagation();
        if ([4, 5].includes(Number(item?.action_type))) {
            setSelectedPendingVCAction(item);
            setPendingVCActionDialogOpen(true);
            return;
        }
        if (Number(item?.action_type) === 2) {
            handleActionDialogOpen();
        } else if (Number(item?.action_type) === 1) {
            handleActionType1DialogOpen();
        }
    };

    const handleRedirectionToVCAction = () => {
        const selectedVC = selectedPendingVCAction || selectedVCModule?.data;
        const selectedZoneDetails = selectedProjectZonesList?.find(
            (zone) => Number(zone?.position) === Number(selectedZoneMain),
        );
        const params = new URLSearchParams();
        const vsId = selectedVC?.vs_id || selectedVSID;
        const vcId = selectedVC?.id || selectedVCModule?.id;

        if (vsId) params.set("vs_id", vsId);
        if (vcId) params.set("vc_id", vcId);
        params.set("actionMode", "vcAction");
        if (selectedZoneMain) {
            params.set(
                "selectedZone",
                selectedZoneDetails?.zone_name || selectedZoneMain,
            );
            params.set("zonePosition", selectedZoneMain);
        }
        if (page) params.set("page", page);

        navigate(`/dashboard/v2/vehicle-component?${params.toString()}`);
        handlePendingVCActionDialogClose();
    };

    const confirmUpdate = async () => {
        const reqObj = {
            current_time: new Date().toISOString(),
            project_id: Number(projectID),
            variant_id: Number(variantID),
            vs_id: Number(selectedVSID),
            vc_id: Number(selectedVCModule?.data?.id),

            is_dependency_linking: false,
        };
        try {
            console.log("confirmUpdate placeholder", reqObj);
            setVcGetTrigger(!vcGetTrigger);
            handleActionDialogClose();
        } catch (error) {
            console.error("Failed to update action type", error);
        }
    };

    const updateChangesAndClose = () => {
        setVcFromUrlState(null);
        handleActionDialogClose();
    };

    const vsParams = useMemo(() => {
        if (!projectID || !variantID) return {};
        return {
            project_id: Number(projectID),
            variant_id: Number(variantID),
            ...(selectedZoneMain && { zone_position: selectedZoneMain }),
            ...(Number(vsFromUrl) && { vs_id: Number(vsFromUrl) }),
            status: true,
            page_number: 1,
            page_size: 10,
        };
    }, [projectID, variantID, selectedZoneMain, vsFromUrl]);

    const searchParams = useMemo(() => {
        if (!selectedVSID) return null;
        return {
            project_id: Number(projectID),
            variant_id: Number(variantID),
            vs_id: Number(selectedVSID),
            ...(Number(vcFromUrlState) && { vc_id: Number(vcFromUrlState) }),
            status: true,
            page_number: page,
            page_size: rowsPerPage,
        };
    }, [projectID, variantID, selectedVSID, page, rowsPerPage, vcFromUrlState]);

    return (
        <Box>
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", pr: 1.4 }}>
                <CustomButtonV2
                    disabled={!projectID || !variantID}
                    btnName="Create Ticket"
                    handleOnClick={() => {
                        navigate(`/dashboard/v2/type-approval/link/create?tab=${1}`);
                    }}
                />
            </Box>

            <SlidingComponentLinkTemplate
                cardOpen={cardOpen}
                setCardOpen={setCardOpen}
                headerTitle={
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 0.5,
                            }}
                        >
                            <Typography>Select Component</Typography>
                            <Box onClick={() => setCardOpen(false)} sx={{ cursor: "pointer" }}>
                                <KeyboardArrowUpIcon />
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            gap={1}
                            sx={{
                                width: "100%",
                                mt: 0.5,
                                gap: 1,
                                alignItems: "center",
                                mr: selectedProjectIsZonal ? 1 : 0,
                            }}
                        >
                            {selectedProjectIsZonal && (
                                <Box sx={{ flex: "0 0 40%" }}>
                                    <CustomSelectV2
                                        label={"Select Zone"}
                                        optionsList={zoneMainOptionsList}
                                        selectedValue={selectedZoneMain}
                                        handleOnChange={(e) => setSelectedZoneMain(e.target.value)}
                                        dark={true}
                                        showLabel={false}
                                        sx={{
                                            height: 40,
                                            border: "1px solid #ced4da",
                                            borderRadius: 1,
                                            background: "#fff",
                                            ...componentStyle.searchField.textField,
                                        }}
                                    />
                                </Box>
                            )}
                            <Box sx={{ flex: selectedProjectIsZonal ? "0 0 58%" : "1" }}>
                                <CustomAutoCompleteWithAPIV2
                                    key={`left-vs-${variantID}-${selectedZoneMain}`}
                                    apiAction={labCar.get_vehicle_systems}

                                    placeholder={"Search Vehicle System"}
                                    firstItemDefaultSelect={true}
                                    skipInitialSearch={false}
                                    toolTipLength={30}
                                    getSelectedItem={(selectedVS) => {
                                        setSelectedVSComponentMain(selectedVS);
                                        setSelectedVSID(selectedVS?.id);       // ✅
                                        if (!selectedVS?.id) {                 // ✅
                                            setVcMainList([]);
                                            return;
                                        }
                                        triggerOnSelectVehicleSystem(selectedVS?.id); // ✅
                                        if (!isMountedRef.current) {
                                            isMountedRef.current = true;
                                        }
                                    }}

                                    displayKey="assembly_number"
                                    searchKey={"assembly_number"}

                                    valueObj={selectedVSComponentMain}
                                    enableDarkMode={true}
                                    txtFldStyle={componentStyle.searchField.textField}
                                    sx={{
                                        height: 40,
                                        border: "1px solid #ced4da",
                                        borderRadius: 1,
                                        background: "#fff",
                                        ...componentStyle.searchField.textField,
                                    }}
                                    skipApiCall={!projectID || !variantID}
                                    params={vsParams}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ width: "100%", mt: 0.5 }}>
                            <CustomSearchbarWithApiV2
                                key={`left-vc-${selectedVSID}`}
                                apiAction={labCar.get_vehicle_components}

                                searchKey={"component_number"}

                                placeholder={"Search Number"}
                                triggerApi={vcGetTrigger}
                                getDataOnSearch={(data, resp) => {
                                    if (resp.error) {
                                        setVcMainList([]);
                                    } else {
                                        setVcMainList(resp.data);
                                        setVcCount(resp?.total_count);
                                        setTotalVehicleComponentCount(resp?.total_count);
                                    }
                                }}
                                skipApiCall={!searchParams}
                                params={searchParams}
                                iconColor={"white"}
                                txtFldStyle={componentStyle.searchField.textField}
                                sx={{
                                    height: 40,
                                    border: "1px solid #ced4da",
                                    borderRadius: 1,
                                    background: "#fff",
                                    ...componentStyle.searchField.textField,
                                }}
                            />
                        </Box>
                    </Box>
                }
                linkedLegalRequirement={linkedLegalRequirement}
                unlinkLegalRequirement={unlinkLegalRequirement}
                handleSelectedComponent={handleSelectedComponent}
                searchKeys={["chapter", "name", "standard_number", "ls_id", "req_number"]}
                dataList={modifiedVCList}
                selectedComponentDetails={selectedVCModule}
                renderCardActionIcons={(item) => (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        {[1, 2, 4, 5].includes(Number(item.action_type)) && (
                            <Box sx={{ position: "absolute", top: "8px", right: "8px" }}>
                                <Tooltip title={getVCActionTypeTooltip(item.action_type)}>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "black" }}
                                        onClick={(event) => handleVCActionTypeClick(event, item)}
                                    >
                                        <ErrorOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                )}
                page={page}
                rowsPerPage={rowsPerPage}
                handlePageChange={(selectedPage) => setPage(selectedPage)}
                totalCount={vcCount}
            />

            <CustomDialogBoxV2
                open={actionDialogOpen}
                onClose={handleActionDialogClose}
                title="Action Required"
                successButtonName="Yes"
                rejectButtonName="No"
                actionButtonStyle="continuous"
                onClickSuccess={confirmUpdate}
                onClickReject={updateChangesAndClose}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        Are you sure the intersection for Vehicle Component is completed?
                    </Typography>
                </Box>
            </CustomDialogBoxV2>

            <CustomDialogBoxV2
                open={actionType1DialogOpen}
                onClose={handleActionType1DialogClose}
                title="Action Pending"
                successButtonName="Link Now"
                rejectButtonName="Link Later"
                actionButtonStyle="continuous"
                onClickSuccess={() => {
                    navigate(
                        `/dashboard/v2/link?project_id=${projectID}&variant_id=${Number(
                            variantID,
                        )}&vs_id=${selectedVSComponentMain?.vs_id}&vc_id=${selectedVCModule.id
                        }&selectedZone=${selectedZoneMain}&tab=3`,
                    );
                    handleActionType1DialogClose();
                }}
                onClickReject={handleActionType1DialogClose}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        Vehicle Component dependency linking is pending!
                    </Typography>
                </Box>
            </CustomDialogBoxV2>

            <CustomDialogBoxV2
                open={pendingVCActionDialogOpen}
                onClose={handlePendingVCActionDialogClose}
                title="Action"
                successButtonName="Link Now"
                rejectButtonName="Link Later"
                actionButtonStyle="continuous"
                onClickSuccess={handleRedirectionToVCAction}
                onClickReject={handlePendingVCActionDialogClose}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        {Number(selectedPendingVCAction?.action_type) === 4
                            ? "Action pending in Vehicle Component Attribute"
                            : "Action pending in Vehicle Component Version"}
                    </Typography>
                </Box>
            </CustomDialogBoxV2>
        </Box>
    );
}

VehicleComponentLegalLink.propTypes = {
    triggerOnSelectVehicleSystem: PropTypes.func,
    selectedVehicleSystem: PropTypes.any,
    setSelectedVehicleSystem: PropTypes.func,
    setTotalVehicleComponentCount: PropTypes.func,
};

VehicleComponentLegalLink.defaultProps = {
    triggerOnSelectVehicleSystem: undefined,
    selectedVehicleSystem: null,
    setSelectedVehicleSystem: undefined,
    setTotalVehicleComponentCount: undefined,
};

export default VehicleComponentLegalLink;
